import { MapBundleMarker } from 'const/Map/MapBundleMarker';
import { flatten } from 'lodash';
import { GeoJSONSource, Map, Popup } from 'mapbox-gl';
import { addSelectedOrdersToBundle } from 'pages/oldPlanner/utils/bundleDraft';
import {
  BundleProperties,
  convertPlannerBundleToGeoBundleOrders,
  getProperties,
} from 'pages/planner/map/utils/mapUtils';
import {
  PlannerBundle,
  PlannerOrder,
  WindowGroups,
} from 'pages/planner/utils/usePlannerData';
import React, { useCallback, useEffect } from 'react';
import { BundleJson } from 'types/bundleTypes';
import { MapMarkerPopup } from '../MarkerPopup/MarkerPopup';

interface BundleMarkerProps {
  map: Map;
  hoverPopupRef: Popup;
  rightClickPopupRef: Popup;
  bundles: PlannerBundle[];
  windowGroups: WindowGroups;
  windows: [string, string][];
  fetchedBundles: BundleJson[];
  selectedOrders: PlannerOrder[];
  selectedOrderIds: string[];
  onSelect: (orders: PlannerOrder[]) => void;
  updateBundleFromPusher: () => void;
}

export const BundleMarker = ({
  map,
  hoverPopupRef,
  rightClickPopupRef,
  onSelect,
  updateBundleFromPusher,
  ...props
}: BundleMarkerProps) => {
  const { bundledOrders, bundles } = convertPlannerBundleToGeoBundleOrders(
    props.bundles
  );

  /* assignLayers */
  /* Create the Layers for the Bundle Marker */
  useEffect(() => {
    /* Wrapper for Bundles */
    if (map.getSource(MapBundleMarker.SourceId)) {
      (map.getSource(MapBundleMarker.SourceId) as GeoJSONSource).setData(
        bundles
      );
    } else {
      map.addSource(MapBundleMarker.SourceId, {
        type: 'geojson',
        data: bundles,
      });
    }

    /* Wrapper for Orders inside Bundles */
    if (map.getSource(MapBundleMarker.OrdersSourceId)) {
      (map.getSource(MapBundleMarker.OrdersSourceId) as GeoJSONSource).setData(
        bundledOrders
      );
    } else {
      map.addSource(MapBundleMarker.OrdersSourceId, {
        type: 'geojson',
        data: bundledOrders,
      });
    }

    /* Remove existing Layers */
    map.getLayer(MapBundleMarker.PointId) &&
      map.removeLayer(MapBundleMarker.PointId);
    map.getLayer(MapBundleMarker.LabelId) &&
      map.removeLayer(MapBundleMarker.LabelId);
    map.getLayer(MapBundleMarker.OrdersPointId) &&
      map.removeLayer(MapBundleMarker.OrdersPointId);
    map.getLayer(MapBundleMarker.OrdersLabelId) &&
      map.removeLayer(MapBundleMarker.OrdersLabelId);

    /* Assign Layers */
    if (bundles.features.length) {
      map.addLayer({
        id: MapBundleMarker.PointId,
        type: 'circle',
        source: MapBundleMarker.SourceId,
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-color': ['get', 'markerColor'],
          'circle-radius': [
            'step',
            ['get', 'numberOfOrders'],
            20,
            50,
            25,
            100,
            30,
          ],
        },
        maxzoom: 12,
      });

      map.addLayer({
        id: MapBundleMarker.LabelId,
        type: 'symbol',
        source: MapBundleMarker.SourceId,
        layout: {
          'text-field': '{numberOfOrders}',
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 12,
        },
        paint: {
          'text-color': ['get', 'textColor'],
        },
        maxzoom: 12,
      });

      map.addLayer({
        id: MapBundleMarker.OrdersPointId,
        type: 'circle',
        source: MapBundleMarker.OrdersSourceId,
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-color': [
            'match',
            ['get', 'window'],
            ...flatten(props.windows),
            '#661aff',
          ],
          'circle-radius': 10,
          'circle-stroke-width': 5,
          'circle-stroke-color': ['get', 'markerColor'],
        },
        minzoom: 12,
      });

      map.addLayer({
        id: MapBundleMarker.OrdersLabelId,
        type: 'symbol',
        source: MapBundleMarker.OrdersSourceId,
        filter: ['>', 'numberOfOrders', 1],
        layout: {
          'text-field': '{numberOfOrders}',
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 12,
        },
        minzoom: 12,
      });
    }
  }, [bundledOrders, bundles, map, props.windows]);

  /* showBundlePolygonOnHover */
  /* Show the Polygon when hover on Bundle */
  useEffect(() => {
    const mouseEnterAction = e => {
      const properties = getProperties<BundleProperties>(
        map,
        MapBundleMarker.PointId,
        e
      );
      const { polygon } = properties;

      if (!map.getSource(MapBundleMarker.PolygonSourceId)) {
        map.addSource(MapBundleMarker.PolygonSourceId, {
          type: 'geojson',
          data: {
            type: 'Feature',
            geometry: {
              type: 'Polygon',
              coordinates: [polygon],
            },
          } as any,
        });

        map.addLayer({
          id: MapBundleMarker.PolygonLayerId,
          type: 'fill',
          source: MapBundleMarker.PolygonSourceId,
          layout: {},
          paint: {
            'fill-color': properties.markerColor,
            'fill-opacity': 0.5,
          },
        });
        map.addLayer({
          id: MapBundleMarker.PolygonOutlineId,
          type: 'line',
          source: MapBundleMarker.PolygonSourceId,
          layout: {},
          paint: {
            'line-color': properties.markerColor,
            'line-width': 1,
          },
        });

        // I want the bundle hover layer to be below the cluster layer/marker
        if (map.getLayer(MapBundleMarker.PointId)) {
          map.moveLayer(
            MapBundleMarker.PolygonOutlineId,
            MapBundleMarker.PointId
          );
          map.moveLayer(
            MapBundleMarker.PolygonLayerId,
            MapBundleMarker.PolygonOutlineId
          );
        }
      }
    };
    map.on('mouseenter', MapBundleMarker.PointId, mouseEnterAction);

    const mouseLeaveAction = function () {
      if (map.getLayer(MapBundleMarker.PolygonOutlineId)) {
        map.removeLayer(MapBundleMarker.PolygonOutlineId);
      }
      if (map.getLayer(MapBundleMarker.PolygonLayerId)) {
        map.removeLayer(MapBundleMarker.PolygonLayerId);
      }
      if (map.getSource(MapBundleMarker.PolygonSourceId)) {
        map.removeSource(MapBundleMarker.PolygonSourceId);
      }
    };
    map.on('mouseleave', MapBundleMarker.PointId, mouseLeaveAction);

    return () => {
      map.off('mouseenter', MapBundleMarker.PointId, mouseEnterAction);
      map.off('mouseleave', MapBundleMarker.PointId, mouseLeaveAction);
    };
  }, [map]);

  /* showBundlePopupOnHover */
  /* Show the Popup when hover on Bundle */
  useEffect(() => {
    const mouseEnterAction = e => {
      map.getCanvas().style.cursor = 'pointer';

      MapMarkerPopup(
        map,
        e.features,
        e.lngLat,
        hoverPopupRef,
        props.selectedOrderIds,
        props.windowGroups,
        props.fetchedBundles,
        onSelect,
        'Overview'
      );
    };
    /* Show the Order information */
    map.on('mouseenter', MapBundleMarker.OrdersPointId, mouseEnterAction);

    const mouseLeaveAction = () => {
      map.getCanvas().style.cursor = '';

      /* Remove Popup */
      hoverPopupRef.remove();
    };
    map.on('mouseleave', MapBundleMarker.OrdersPointId, mouseLeaveAction);

    return () => {
      map.off('mouseenter', MapBundleMarker.OrdersPointId, mouseEnterAction);
      map.off('mouseleave', MapBundleMarker.OrdersPointId, mouseLeaveAction);
    };
  }, [
    hoverPopupRef,
    map,
    onSelect,
    props.fetchedBundles,
    props.selectedOrderIds,
    props.windowGroups,
  ]);

  /* onRightClick */
  useEffect(() => {
    const contextMenuAction = e => {
      map.getCanvas().style.cursor = 'pointer';

      /* Add Popup */
      MapMarkerPopup(
        map,
        e.features,
        e.lngLat,
        rightClickPopupRef,
        props.selectedOrderIds,
        props.windowGroups,
        props.fetchedBundles,
        onSelect,
        'Edit'
      );
    };
    /* Show the Bundle information with all orders list */
    map.on('contextmenu', MapBundleMarker.OrdersPointId, contextMenuAction);

    return () => {
      map.off('contextmenu', MapBundleMarker.OrdersPointId, contextMenuAction);
    };
  }, [
    map,
    onSelect,
    props.fetchedBundles,
    props.windowGroups,
    rightClickPopupRef,
    props.selectedOrderIds,
  ]);

  const addSelectedOrdersToBundleOfClickedMarker = useCallback(
    (bundleId: string) => {
      if (props.selectedOrderIds.length) {
        addSelectedOrdersToBundle(bundleId, props.selectedOrders)
          .then(() => {
            updateBundleFromPusher();
            const bundleDraftElement = document.getElementById(bundleId);
            bundleDraftElement?.scrollIntoView({ behavior: 'smooth' });
            bundleDraftElement?.classList.add('map-selection-animation');
          })
          .catch(error => {
            console.error(error);
            /* Update the bundle in the Map */
            /* because usually the error is saying `Order XXX is already in bundle XXX.` */
            updateBundleFromPusher();
          });
      }
    },
    [
      props.selectedOrders,
      props.selectedOrderIds.length,
      updateBundleFromPusher,
    ]
  );

  /* shiftAltClick */
  useEffect(() => {
    const orderOfBundleClicked = e => {
      /* Max : Shift + Option + Click Bundle Marker */
      /* Windows : Shift + Alt + Click Bundle Marker */
      /* Add the `Selected Order` values to this Bundle of Clicked Order */
      if (e.originalEvent.altKey && e.originalEvent.shiftKey) {
        const { bundleId } = JSON.parse(e?.features[0]?.properties?.orders)[0];
        if (bundleId) {
          addSelectedOrdersToBundleOfClickedMarker(bundleId);
        }
      }
    };
    map.on('click', MapBundleMarker.OrdersPointId, orderOfBundleClicked);

    const bundleClicked = e => {
      /* Max : Shift + Option + Click Bundle's Orders Marker */
      /* Windows : Shift + Alt + Click Bundle's Orders Marker */
      /* Add the `Selected Order` values to this clicked Bundle */
      if (e.originalEvent.altKey && e.originalEvent.shiftKey) {
        const properties = getProperties<BundleProperties>(
          map,
          MapBundleMarker.PointId,
          e
        );
        const { bundleId } = properties;
        if (bundleId) {
          addSelectedOrdersToBundleOfClickedMarker(String(bundleId));
        }
      }
    };
    map.on('click', MapBundleMarker.PointId, bundleClicked);

    return () => {
      map.off('click', MapBundleMarker.OrdersPointId, orderOfBundleClicked);
      map.off('click', MapBundleMarker.PointId, bundleClicked);
    };
  }, [addSelectedOrdersToBundleOfClickedMarker, map]);

  return <></>;
};
